import React from "react";
import {
  IconButton,
  Dialog as BaseDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  CircularProgress,
  Box,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { withStyles } from "@mui/styles";
import { Button } from "./Button";
import { Typography } from "./Typography";
import { CloseIcon, DeleteRowIcon } from "../../providers/RexIconsSVG/RexIconsSVG";
import classnames from "classnames";
import { useI18n } from "../../providers/I18n/I18n";
import {
  CheckBoxIcon,
  UnCheckBoxIcon,
} from "../../providers/RexIconsSVG/RexIconsSVG";
import { useStyles } from "./style";

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxHeight: "calc(100vh - 200px)",
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const textFormatHelper = (val: string) => {
  return val.toLowerCase().replace(/^.{1}/g, val[0].toUpperCase());
};

export const SmallDialog = ({
  onClose,
  onSave,
  onReset,
  dialogOptions: { open, maxWidth, fullWidth },
  saveButtonValue,
  closeButtonValue,
  resetButtonValue,
  title,
  subtitle = "",
  isLoading,
  hideCreateAnother,
  customDialogContentStyle,
  showAddBtn,
  ShowCustomButtonsLayoutStyle,
  customButtonsLayoutStyle,
  customButtonsLayout,
  children,
  disableVerticalScroll,
  hideDialogActions = false,
  disableSave = false,
  saveButtonCustomClass,
  createAnother,
  createAnotherHandler,
  showDeleteIcon,
  setDeleteClicked,
  customToolbarComponent = <></>,
}: {
  onClose?: () => void;
  onSave?: () => Promise<void> | void;
  onReset?: () => void;
  dialogOptions?: Record<string, any>;
  saveButtonValue?: string;
  closeButtonValue?: string;
  resetButtonValue?: string;
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
  hideCreateAnother?: boolean;
  isValid?: boolean;
  customDialogContentStyle?: string;
  showAddBtn?: boolean;
  ShowCustomButtonsLayoutStyle?: boolean;
  customButtonsLayoutStyle?: string;
  customButtonsLayout?: React.ReactNode;
  children?: React.ReactNode;
  disableVerticalScroll?: boolean;
  hideDialogActions?: boolean;
  disableSave?: boolean;
  saveButtonCustomClass?: string;
  createAnother?: boolean;
  createAnotherHandler?: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteIcon?: boolean;
  setDeleteClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  customToolbarComponent?: JSX.Element;
}) => {
  const classes = useStyles();
  const { t } = useI18n();

  const handleClose = (_, reason) => {
    if (["escapeKeyDown"].includes(reason)) {
      onClose();
    }
  };

  const SaveButton = () => {
    return (
      onSave && (
        <Button
          className={classnames(classes.addButton, saveButtonCustomClass)}
          onClick={async () => await onSave()}
          disabled={isLoading || disableSave}
          testId="small-dialog-save-button"
        >
          {isLoading ? (
            <CircularProgress size={22} className={classes.loginLoader} />
          ) : showAddBtn ? (
            textFormatHelper(t("add"))
          ) : (
            saveButtonValue
          )}
        </Button>
      )
    );
  };

  const CloseButton = () => {
    return (
      onClose && (
        <Button
          variant="outlined"
          onClick={() => {
            onClose();
          }}
          className={classes.cancelButton}
          testId="small-dialog-cancel-button"
        >
          {closeButtonValue || textFormatHelper(t("cancel"))}
        </Button>
      )
    );
  };

  const ResetButton = () => {
    return (
      onReset && (
        <Button
          onClick={onReset}
          color="secondary"
          className={classes.cancelButton}
          testId="resetButton"
        >
          {resetButtonValue || "Reset"}
        </Button>
      )
    );
  };

  const ButtonsLayoutHelper = () => {
    if (customButtonsLayout) return customButtonsLayout;
    if (ShowCustomButtonsLayoutStyle) {
      return (
        <Grid container className={customButtonsLayoutStyle}>
          <Grid item>{CloseButton()}</Grid>
          <Grid item>
            {ResetButton()}
            {SaveButton()}
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container justifyContent="flex-end">
          <Grid item> {CloseButton()}</Grid>
          <Grid item> {SaveButton()}</Grid>
          <Grid item>{ResetButton()}</Grid>
        </Grid>
      );
    }
  };

  return (
    <BaseDialog
      scroll="body"
      disableScrollLock={true}
      onClose={handleClose}
      open={open}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      classes={{
        paper: disableVerticalScroll ? classes.smallDialogPaper : "",
      }}
    >
      <div data-testid={"dialog-wrapper"}>
        <MuiDialogTitle className={classes.dialog}>
          <Box display="flex" justifyContent="space-between">
            <Typography
              variant="h6"
              className={classes.popheading}
              data-testid="small_dialog_title"
            >
              {title}
            </Typography>
            {showDeleteIcon && (
              <IconButton
                aria-label="delete"
                onClick={() => setDeleteClicked && setDeleteClicked(true)}
                sx={{
                  position: "absolute",
                  right: 50,
                  top: 9,
                }}
              >
                <DeleteRowIcon className={classes.deleteIcon} viewBox="0 0 28 28" />
                <Typography className={classes.delete}>Delete</Typography>
              </IconButton>
            )}
            <Box>
              {customToolbarComponent}
              {onClose ? (
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    // position: "absolute",
                    // right: 8,
                    // top: 10,
                    height: 25,
                    width: 25,
                    color: (theme) => theme.palette.grey[500],
                  }}
                  data-testid="close_dialog"
                >
                  <CloseIcon
                    className={classes.closeIcon}
                    data-testid="close_dialog"
                    viewBox="0 0 14 14"
                  />
                </IconButton>
              ) : null}
            </Box>
          </Box>

          {!!subtitle.length && (
            <Typography variant="subtitle2">{subtitle}</Typography>
          )}
        </MuiDialogTitle>

        <DialogContent
          dividers
          classes={{
            root: customDialogContentStyle,
          }}
          // style={{paddingRight: "2px"}}
        >
          {children}
        </DialogContent>
        {!hideDialogActions && (
          <DialogActions>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                {!hideCreateAnother && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        inputProps={
                          {
                            "data-testid": "create_another",
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        icon={<UnCheckBoxIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        checked={createAnother}
                        classes={{ root: classes.belowNormalInput }}
                        onClick={() =>
                          createAnotherHandler((prevState) => !prevState)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <div className={classes.checkLabel}>{t("create_another")}</div>
                    }
                  />
                )}
              </Grid>
              <Grid item xs={hideCreateAnother && 12}>
                {ButtonsLayoutHelper()}
              </Grid>
            </Grid>
          </DialogActions>
        )}
      </div>
    </BaseDialog>
  );
};
